import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { InfoTooltip } from '@/components/common/InfoTooltip';
import { BAR_COLORS } from '@/utils/chart-colors';

interface VersionDistributionProps {
  versionDistribution: Record<string, number>;
  latestVersion: string | null;
  selectedVersion?: string | null;
  onSelect?: (version: string | null) => void;
}

export function VersionDistribution({
  versionDistribution,
  latestVersion,
  selectedVersion,
  onSelect,
}: VersionDistributionProps) {
  const data = Object.entries(versionDistribution)
    .map(([version, count]) => ({
      version: version === 'Unknown' ? 'Unknown' : version,
      count,
      isLatest: version === latestVersion,
    }))
    .sort((a, b) => b.count - a.count);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="dls-flex dls-items-center dls-gap-1.5">
          Version
          <InfoTooltip text="Count of Nodes per software version. Click a bar to filter the dashboard by that version." />
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="dls-h-[250px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={data}
              layout="vertical"
              onClick={(state) => {
                if (onSelect && state?.activePayload?.[0]?.payload?.version) {
                  const clicked = state.activePayload[0].payload.version;
                  onSelect(clicked === selectedVersion ? null : clicked);
                }
              }}
            >
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" />
              <YAxis dataKey="version" type="category" width={120} fontSize={12} />
              <Tooltip
                formatter={(value: number) => [`${value} Nodes`, 'Count']}
                labelFormatter={(label) => `Version: ${label}`}
                cursor={{ fill: 'rgba(0,0,0,0.05)', cursor: 'pointer' }}
              />
              <Bar
                dataKey="count"
                radius={[0, 4, 4, 0]}
                barSize={20}
                className={onSelect ? 'dls-cursor-pointer hover:dls-opacity-80' : ''}
              >
                {data.map((_, index) => (
                  <Cell key={`cell-${index}`} fill={BAR_COLORS[index % BAR_COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        {latestVersion && (
          <p className="dls-text-xs dls-text-muted-foreground dls-mt-2">
            Latest version: <span className="dls-font-mono dls-font-medium">{latestVersion}</span>
          </p>
        )}
      </CardContent>
    </Card>
  );
}
